import { getMuzzlePosition } from '../game/combat'
import type { SimulationEvent, SimulationState } from '../game/types'
import { WEAPON_MOUNT } from '../game/weapon-mount'

export type CombatParticleKind = 'smoke' | 'casing' | 'spark' | 'ring'

export interface CombatParticleSeed {
  kind: CombatParticleKind
  id: number
  position: { x: number; y: number; z: number }
  velocity: { x: number; y: number; z: number }
  ttl: number
  spin: number
}

export function reconcileCombatEffectParticles<T>(nextEventId: number, lastEventId: number, particles: T[]): T[] {
  return nextEventId > lastEventId ? particles : []
}

function seededUnit(seed: number): number {
  const value = Math.sin(seed * 91.345 + 17.719) * 47_358.5453
  return value - Math.floor(value)
}

export function createCasingVelocity(eventId: number, heading: number): CombatParticleSeed['velocity'] {
  const sideSpeed = 2.7 + seededUnit(eventId) * 1.4
  const forwardSpeed = -0.6 + seededUnit(eventId + 31) * 1.2
  const sideX = Math.cos(heading)
  const sideZ = -Math.sin(heading)
  const forwardX = Math.sin(heading)
  const forwardZ = Math.cos(heading)
  return {
    x: sideX * sideSpeed + forwardX * forwardSpeed,
    y: 2.2 + seededUnit(eventId + 73) * 1.8,
    z: sideZ * sideSpeed + forwardZ * forwardSpeed,
  }
}

function createSparkBurst(
  event: SimulationEvent,
  position: { x: number; y: number },
  count: number,
  idOffset = 0,
): CombatParticleSeed[] {
  return Array.from({ length: count }, (_, index) => {
    const angle = seededUnit(event.id * 19 + index * 7 + idOffset) * Math.PI * 2
    const speed = 4.5 + seededUnit(event.id * 31 + index * 13 + idOffset) * 6
    return {
      kind: 'spark' as const,
      id: event.id * 32 + idOffset + index,
      position: { x: position.x, y: 0.9 + (index % 3) * 0.2, z: position.y },
      velocity: {
        x: Math.sin(angle) * speed,
        y: 2.4 + seededUnit(event.id + index * 23 + idOffset) * 5,
        z: Math.cos(angle) * speed,
      },
      ttl: 0.18 + seededUnit(event.id + index * 29 + idOffset) * 0.22,
      spin: seededUnit(event.id + index * 37 + idOffset) * Math.PI * 2,
    }
  })
}

export function createCombatParticles(
  event: SimulationEvent,
  state: Pick<SimulationState, 'vehicles'>,
): CombatParticleSeed[] {
  if (event.type === 'fire') {
    const shooter = state.vehicles.find((vehicle) => vehicle.id === event.vehicleId)
    if (!shooter) return []
    const muzzle = getMuzzlePosition(shooter)
    const forwardX = Math.sin(shooter.heading)
    const forwardZ = Math.cos(shooter.heading)
    const origin = { x: muzzle.x, y: WEAPON_MOUNT.muzzle[1], z: muzzle.y }
    return [
      {
        kind: 'smoke',
        id: event.id * 16,
        position: { ...origin },
        velocity: { x: forwardX * 1.5, y: 0.8, z: forwardZ * 1.5 },
        ttl: 0.52,
        spin: seededUnit(event.id + 7) * Math.PI * 2,
      },
      {
        kind: 'casing',
        id: event.id * 16 + 1,
        position: { x: origin.x - forwardX * 0.38, y: origin.y + 0.08, z: origin.z - forwardZ * 0.38 },
        velocity: createCasingVelocity(event.id, shooter.heading),
        ttl: 1.15,
        spin: 8 + seededUnit(event.id + 11) * 11,
      },
    ]
  }

  if (event.type === 'hit' && event.targetId) {
    const target = state.vehicles.find((vehicle) => vehicle.id === event.targetId)
    if (!target) return []
    return createSparkBurst(event, target.position, 7)
  }

  if (event.type === 'driver-hit') {
    const target = state.vehicles.find((vehicle) => vehicle.id === event.vehicleId)
    if (!target) return []
    return [
      {
        kind: 'ring',
        id: event.id * 32,
        position: { x: target.position.x, y: 1.1, z: target.position.y },
        velocity: { x: 0, y: 0.35, z: 0 },
        ttl: 0.48,
        spin: 0,
      },
      ...createSparkBurst(event, target.position, 10, 1),
    ]
  }

  if (event.type === 'vehicle-contact' && event.targetId) {
    const first = state.vehicles.find((vehicle) => vehicle.id === event.vehicleId)
    const second = state.vehicles.find((vehicle) => vehicle.id === event.targetId)
    if (!first || !second) return []
    const midpoint = {
      x: (first.position.x + second.position.x) * 0.5,
      y: (first.position.y + second.position.y) * 0.5,
    }
    const dust = Array.from(
      { length: 3 },
      (_, index): CombatParticleSeed => ({
        kind: 'smoke',
        id: event.id * 32 + index,
        position: { x: midpoint.x, y: 0.24 + index * 0.08, z: midpoint.y },
        velocity: {
          x: (seededUnit(event.id + index * 11) - 0.5) * 3,
          y: 0.8 + seededUnit(event.id + index * 17),
          z: (seededUnit(event.id + index * 23) - 0.5) * 3,
        },
        ttl: 0.38 + index * 0.05,
        spin: seededUnit(event.id + index * 31) * Math.PI * 2,
      }),
    )
    return [...dust, ...createSparkBurst(event, midpoint, 6, 8)]
  }

  if (event.type === 'rail-break') {
    const vehicle = state.vehicles.find((candidate) => candidate.id === event.vehicleId)
    if (!vehicle) return []
    return createSparkBurst(event, vehicle.position, 14, 12)
  }

  if (event.type === 'vehicle-explode') {
    const vehicle = state.vehicles.find((candidate) => candidate.id === event.vehicleId)
    const position = event.position ?? vehicle?.position
    if (!position) return []
    const smoke = Array.from(
      { length: 8 },
      (_, index): CombatParticleSeed => ({
        kind: 'smoke',
        id: event.id * 64 + index,
        position: { x: position.x, y: 0.5 + index * 0.12, z: position.y },
        velocity: {
          x: (seededUnit(event.id + index * 5) - 0.5) * 8,
          y: 2 + seededUnit(event.id + index * 11) * 5,
          z: (seededUnit(event.id + index * 17) - 0.5) * 8,
        },
        ttl: 0.8 + seededUnit(event.id + index * 23) * 0.6,
        spin: seededUnit(event.id + index * 29) * Math.PI * 2,
      }),
    )
    return [
      ...smoke,
      ...createSparkBurst(event, position, 24, 16),
      {
        kind: 'ring',
        id: event.id * 64 + 63,
        position: { x: position.x, y: 0.8, z: position.y },
        velocity: { x: 0, y: 0.4, z: 0 },
        ttl: 0.72,
        spin: 0,
      },
    ]
  }

  return []
}

export function appendCappedParticles<T extends CombatParticleSeed>(existing: T[], incoming: T[], cap: number): T[] {
  if (cap <= 0) return []
  return [...existing, ...incoming].slice(-cap)
}
